import { useEffect, useRef, useState, useCallback } from "react";
import { Video, Loader2, AlertTriangle, ExternalLink, RotateCcw, PhoneOff, Maximize, Minimize } from "lucide-react";
import { useRequestJoinMaterial, detailMessage } from "@/lib/zoomQueries";

/**
 * Master v5 Prompt 18A — the Zoom Meeting SDK Component View, embedded
 * as a video/call dock alongside the existing resource tabs inside
 * SessionRoomRenderer.jsx. Deliberately its own, independent component:
 * a Zoom disconnect/error/leave never touches WorkDocumentRoomView's own
 * autosave/lease state next to it, and Zoom start/join/end is never
 * Session completion (Complete Session, Prompt 18, is the only workflow
 * that can do that).
 *
 * Fix (testing agent, iteration 54, CRITICAL): @zoom/meetingsdk 6.2.0's
 * Component View bundle dereferences React 18 internals that React 19
 * (this app's version) removed, so importing the SDK directly here
 * crashed client.init() before any request reached zoom.us — reproduced
 * live for both coach and client. Fix: the SDK now runs inside
 * /public/zoom-embed.html, a same-origin but isolated iframe with its
 * own React 18 vendor bundle loaded from Zoom's CDN — exactly the setup
 * proven live (real wss media/signal sockets, Zoom's recording-consent
 * dialog rendered). Join material crosses the postMessage boundary only
 * once per attempt; the SDK Key/secret/JWT never touch this iframe,
 * only the already-signed `signature`. UNCHANGED by the layout fix
 * below — this comment block stays accurate.
 *
 * Layout rewrite (user-reported, "Fix Embedded Zoom Frame Sizing +
 * Sticky Session Layout") — root cause of the old overflow/mis-sizing
 * reports: this dock used to rip itself out of normal flow with
 * `position: fixed` once in a meeting, centered on the raw viewport
 * (`left: 50%, translateX(-50%)`, width `min(56rem, 100vw - 2rem)`) —
 * completely disconnected from SessionRoomRenderer's actual Zoom-area
 * slot, so that slot was never really "the source of truth" for size.
 * Worse, once a coach dragged the old resize handle, the dock's pinned
 * width became a bare `${frameSize.width}px` with NO `max-width`
 * safety net and no re-clamp on a later window resize — shrinking the
 * browser after a drag would genuinely overflow it past the viewport
 * edge. Fixed by:
 *   - Rendering in NORMAL FLOW always (no position:fixed, no spacer
 *     div, no captured pinnedTop) — SessionRoomRenderer now gives this
 *     dock a shrink-0 slot at the top of a flex-col whose only sibling
 *     (the Resource area) is the ONE scrollable region, so the dock
 *     stays visible while that area scrolls for free, no pinning trick
 *     needed at all.
 *   - Always `w-full max-w-full` — width can never exceed its parent,
 *     by construction, not by a clamp that could have edge cases.
 *   - A CSS `aspect-video` box by default (responsive: recalculates
 *     with the container's width via pure CSS, no JS math), capped at
 *     `max-h-[55vh]` so it can never crowd out the Resource area below.
 *   - The resize handle is now vertical-only (drag to make the video
 *     area taller/shorter) — width dragging is gone entirely, which is
 *     what removes the overflow class of bug above at the root instead
 *     of patching around it.
 *   - A ResizeObserver on the iframe's own immediate wrapper (not a
 *     step removed on the outer dock) drives a debounced (120ms)
 *     "zoom.resize" postMessage — avoids flooding the SDK with
 *     updateVideoOptions() calls during a continuous window/handle
 *     drag, per this task's explicit ask.
 *   - True browser Fullscreen API entry/exit (`dockRef.
 *     requestFullscreen()`) is UNCHANGED — it already covers the whole
 *     viewport independent of this component's own flex layout, and
 *     still immediately re-sends a resize on toggle.
 *
 * Desktop: embedded Component View via the isolated iframe above.
 * Mobile web: Component View is not a verified-supported path, so a
 * narrow viewport goes straight to the external-join fallback instead
 * of attempting an embed that may silently fail.
 */
const MOBILE_BREAKPOINT_PX = 768;
const ZOOM_EMBED_FRAME_SRC = "/zoom-embed.html";
const DEFAULT_HEIGHT = 400; // first-paint fallback only, before any real box has ever been measured
const MIN_HEIGHT = 280;
const MAX_HEIGHT_RATIO = 0.55; // fix (testing_agent, iteration 79, MEDIUM): 0.8 left ~0px for the Resource area on short viewports; 0.55 keeps Zoom generously sized while guaranteeing the sibling Resource area a real share of the viewport
const RESIZE_DEBOUNCE_MS = 120;

function isLikelyMobile() {
  if (typeof window === "undefined") return false;
  const uaMobile = /iphone|ipad|ipod|android|mobile/i.test(window.navigator.userAgent || "");
  return uaMobile || window.innerWidth < MOBILE_BREAKPOINT_PX;
}

function clampHeight(h) {
  const maxH = typeof window !== "undefined" ? window.innerHeight * MAX_HEIGHT_RATIO : 720;
  return Math.max(MIN_HEIGHT, Math.min(maxH, h));
}

function externalJoinUrl(meetingNumber, password) {
  if (!meetingNumber) return null;
  const params = password ? `?pwd=${encodeURIComponent(password)}` : "";
  return `https://zoom.us/j/${meetingNumber}${params}`;
}

export default function ZoomMeetingDock({ workspaceId, sesId, roomState, meetingAvailable, isCoach }) {
  // idle -> requesting -> joining -> in_meeting -> ended | error
  const [phase, setPhase] = useState("idle");
  const [errorMessage, setErrorMessage] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  // null until the coach drags the corner handle — while null the
  // video area is a responsive `aspect-video` box (the "Perfect Fit"
  // default); once set, it's pinned to the exact height the coach
  // chose (still always `w-full` of its column either way).
  const [frameHeight, setFrameHeight] = useState(null);
  const [isResizing, setIsResizing] = useState(false);
  // Fix (post-testing-agent, MEDIUM): a failed join-material request has
  // no `material` yet, but the backend now includes a fallback {meeting_
  // number, password} in the 503 body precisely so a ZAK/OAuth outage
  // never leaves the user with only "Retry" and no external-join option.
  const [fallbackJoin, setFallbackJoin] = useState(null);
  // Remounted (fresh key) on every join attempt so a retry always gets a
  // brand-new iframe/SDK client rather than reusing one that already
  // failed or already consumed its one-time join message.
  const [frameKey, setFrameKey] = useState(0);
  const iframeRef = useRef(null);
  const dockRef = useRef(null);
  // Fix (testing_agent, iteration 79, HIGH): a plain useRef never
  // changes identity, so an effect depending on [sendResize] alone
  // never re-ran when this node actually mounted (only once phase
  // becomes "joining", conditionally) — the ResizeObserver below was
  // silently never created until an unrelated fullscreen toggle
  // happened to re-run the effect. A callback ref that mirrors the
  // node into state makes the mount/unmount itself a real dependency.
  const [iframeWrapNode, setIframeWrapNode] = useState(null);
  const iframeWrapRef = useCallback((node) => setIframeWrapNode(node), []);
  const pendingJoinRef = useRef(null);
  const lastMaterialRef = useRef(null);
  const resizingRef = useRef(null);
  const resizeTimerRef = useRef(null);
  const requestJoinMaterial = useRequestJoinMaterial(workspaceId, sesId);
  // Master v5 Prompt 31 — the coach may open/test the embedded meeting
  // any time before it ends/is cancelled (mirrors the Room-level
  // authorization relaxation); a client stays gated to the exact same
  // join-window rule as before.
  const canJoinNow = isCoach ? (roomState !== "ended" && roomState !== "cancelled") : (roomState === "join_window" || roomState === "live");
  const mobileFallback = isLikelyMobile();

  const sendToFrame = useCallback((message) => {
    iframeRef.current?.contentWindow?.postMessage(message, window.location.origin);
  }, []);

  const sendResize = useCallback(() => {
    if (isFullscreen) {
      // No payload = "forget any explicit box, measure your own native
      // window size" — true fullscreen genuinely fills the viewport, so
      // the iframe's own dimensions are already correct.
      sendToFrame({ type: "zoom.resize" });
      return;
    }
    const rect = iframeRef.current?.getBoundingClientRect();
    if (rect && rect.width > 0 && rect.height > 0) {
      sendToFrame({ type: "zoom.resize", width: Math.round(rect.width), height: Math.round(rect.height) });
    }
  }, [isFullscreen, sendToFrame]);

  useEffect(() => {
    function onMessage(event) {
      // Same-origin iframe: exact origin AND exact source window match,
      // never a wildcard — a stray postMessage from another tab/frame
      // must never be treated as a Zoom SDK event.
      if (event.origin !== window.location.origin) return;
      if (!iframeRef.current || event.source !== iframeRef.current.contentWindow) return;
      const data = event.data || {};
      if (data.source !== "zoom-embed-frame") return;

      if (data.type === "ready") {
        if (pendingJoinRef.current) sendToFrame({ type: "zoom.join", ...pendingJoinRef.current });
      } else if (data.type === "joined") {
        setPhase("in_meeting");
        // Defensive re-size the instant the SDK reports joined, in case
        // the box changed while the iframe was still connecting.
        requestAnimationFrame(sendResize);
      } else if (data.type === "error") {
        // Diagnostics (testing agent, iteration 54): distinguish "the
        // embedded client itself couldn't start" from "Zoom refused the
        // join" instead of one generic browser-blaming message.
        console.error("[ZoomMeetingDock] embedded join failed:", data.detail?.code, data.detail?.message);
        setPhase("error");
        setErrorMessage(
          data.detail?.code === "join_failed"
            ? "Zoom couldn't connect to this meeting — try the link below instead."
            : "Couldn't start the embedded meeting client — try the link below instead."
        );
        if (lastMaterialRef.current) setFallbackJoin(lastMaterialRef.current);
      } else if (data.type === "ended") {
        // The SDK's own leave/end control was used, or the connection
        // dropped — never treated as Session completion. Saved
        // WorkDocument state next to this dock is untouched.
        setPhase("ended");
      }
    }
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [sendToFrame, sendResize]);

  useEffect(() => {
    return () => {
      // Leaving the Room unmounts this dock — never leaves a Zoom
      // session silently connected in the background.
      sendToFrame({ type: "zoom.leave" });
    };
  }, [sendToFrame]);

  // Fix ("Fix Embedded Zoom Frame Sizing"): attached directly to the
  // iframe's own immediate wrapper (the actual Zoom viewport/container
  // this task asks for) rather than a step removed on the outer dock.
  // Debounced so a continuous window/handle drag doesn't flood the SDK
  // with updateVideoOptions() calls or risk a ResizeObserver loop.
  useEffect(() => {
    if (!iframeWrapNode || typeof ResizeObserver === "undefined") return;
    let cancelled = false;
    const ro = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry || cancelled) return;
      const { width, height } = entry.contentRect;
      if (width <= 0 || height <= 0) return; // guard: a zero-size container mid-transition is never a real resize
      if (resizeTimerRef.current) clearTimeout(resizeTimerRef.current);
      resizeTimerRef.current = setTimeout(() => {
        if (!cancelled) sendResize();
      }, RESIZE_DEBOUNCE_MS);
    });
    ro.observe(iframeWrapNode);
    return () => {
      cancelled = true;
      ro.disconnect();
      if (resizeTimerRef.current) clearTimeout(resizeTimerRef.current);
    };
  }, [iframeWrapNode, sendResize]);

  // Fullscreen enter/exit is a discrete, infrequent user action (not a
  // continuous drag) — resize immediately, don't wait for the debounce.
  useEffect(() => {
    if (phase !== "in_meeting" && phase !== "joining") return;
    sendResize();
  }, [isFullscreen, phase, sendResize]);

  async function handleJoin() {
    setErrorMessage(null);
    setFallbackJoin(null);
    setPhase("requesting");
    let material;
    try {
      material = await requestJoinMaterial.mutateAsync();
    } catch (err) {
      setPhase("error");
      setErrorMessage(detailMessage(err.body, "Couldn't prepare this meeting — please try again."));
      if (err.body?.detail?.meeting_number) {
        setFallbackJoin({ meetingNumber: err.body.detail.meeting_number, password: err.body.detail.password });
      }
      return;
    }

    if (mobileFallback) {
      // Fix (testing agent, iteration 54, HIGH): window.open(url,
      // "_blank", "noopener,noreferrer") returns null BY SPEC whenever
      // "noopener" is passed, so `if (!opened)` was always true even
      // when the Zoom tab genuinely opened — the mobile_redirected state
      // was dead code and every mobile user saw a false "blocked"
      // error. Open without "noopener" and null the returned window's
      // own opener reference ourselves for the same anti-tabnabbing
      // protection, while still being able to detect a real block.
      const opened = window.open(externalJoinUrl(material.meeting_number, material.password), "_blank");
      if (!opened) {
        setPhase("error");
        setErrorMessage("Your browser blocked the Zoom tab — use the link below to open it manually.");
        setFallbackJoin({ meetingNumber: material.meeting_number, password: material.password });
        return;
      }
      opened.opener = null;
      setPhase("mobile_redirected");
      return;
    }

    const initRect = dockRef.current?.getBoundingClientRect();
    const initWidth = Math.round(initRect?.width || 720);
    lastMaterialRef.current = { meetingNumber: material.meeting_number, password: material.password };
    pendingJoinRef.current = {
      signature: material.signature, meetingNumber: material.meeting_number,
      password: material.password || "", userName: material.user_name, zak: material.zak || undefined,
      width: initWidth,
      height: frameHeight || clampHeight(Math.round(initWidth * 9 / 16)) || DEFAULT_HEIGHT,
    };
    setFrameKey((k) => k + 1);
    setPhase("joining");
  }

  function handleLeave() {
    sendToFrame({ type: "zoom.leave" });
    setPhase("ended");
  }

  useEffect(() => {
    function onFullscreenChange() { setIsFullscreen(document.fullscreenElement === dockRef.current); }
    document.addEventListener("fullscreenchange", onFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", onFullscreenChange);
  }, []);

  function toggleFullscreen() {
    if (document.fullscreenElement) document.exitFullscreen();
    else dockRef.current?.requestFullscreen?.();
  }

  // Fix (testing agent, iteration 72, HIGH, "Resizable Frame"): the old
  // window-level mousemove/mouseup handlers stopped receiving events the
  // instant the cursor crossed over the same-origin Zoom iframe (the
  // iframe's own document captured them instead), so a drag barely
  // registered and its cleanup (mouseup) was silently lost, leaving the
  // dock stuck mid-resize. Pointer Events + setPointerCapture on the
  // handle itself route every subsequent pointer event back to that
  // exact element regardless of what's under the cursor, and
  // pointer-events:none on the iframe while dragging is a second,
  // independent safety net against the same problem.
  //
  // Layout rewrite: vertical-only now (width is always w-full of the
  // Zoom area, never manually set) — this alone removes the class of
  // "manually resized past the container's real width" overflow bug the
  // old width-dragging version was exposed to.
  const onResizeMove = useCallback((e) => {
    const r = resizingRef.current;
    if (!r) return;
    setFrameHeight(clampHeight(r.startHeight + (e.clientY - r.startY)));
  }, []);
  function stopResize(e) {
    resizingRef.current = null;
    setIsResizing(false);
    e?.currentTarget?.releasePointerCapture?.(e.pointerId);
    // Belt-and-braces (testing_agent, iteration 79): the ResizeObserver
    // above already reacts to this frameHeight change, but this direct
    // call guarantees the SDK is told about the final size even if a
    // browser were to (atypically) coalesce/skip the observer callback.
    requestAnimationFrame(sendResize);
  }
  function startResize(e) {
    e.preventDefault();
    const rect = iframeWrapNode?.getBoundingClientRect();
    if (!rect) return;
    resizingRef.current = { startY: e.clientY, startHeight: rect.height };
    setIsResizing(true);
    e.currentTarget.setPointerCapture(e.pointerId);
  }

  if (!meetingAvailable) {
    return (
      <div className="mb-5 rounded-xl border border-ink/10 bg-white/40 p-3 flex items-center gap-2" data-testid="zoom-dock-not-configured">
        <Video size={14} className="text-ink/50" />
        <p className="text-xs text-ink/70">{isCoach ? "You haven't set up an embedded meeting for this session yet." : "Your coach hasn\u2019t set up an embedded meeting for this session yet."}</p>
      </div>
    );
  }

  if (!canJoinNow && phase === "idle") {
    return (
      <div className="mb-5 rounded-xl border border-ink/10 bg-white/40 p-3 flex items-center gap-2" data-testid="zoom-dock-too-early">
        <Video size={14} className="text-ink/50" />
        <p className="text-xs text-ink/70">The meeting join button will appear here once the join window opens.</p>
      </div>
    );
  }

  return (
    <div
      ref={dockRef}
      className={
        isFullscreen
          ? "bg-ink overflow-hidden w-screen h-screen flex flex-col"
          : "w-full max-w-full rounded-xl border border-ink/10 bg-white/60 overflow-hidden relative mb-4"
      }
      data-testid="zoom-meeting-dock"
    >
      {phase === "idle" && (
        <button
          onClick={handleJoin}
          className="w-full flex items-center justify-center gap-2 py-3 text-sm font-semibold text-paper bg-terracotta hover:bg-terracotta/90 transition-colors"
          data-testid="zoom-dock-join-button"
        >
          <Video size={15} /> Join Meeting
        </button>
      )}

      {phase === "requesting" && (
        <div className="flex items-center justify-center gap-2 py-6 text-sm text-ink/70" data-testid="zoom-dock-connecting">
          <Loader2 size={15} className="animate-spin" /> Preparing your meeting…
        </div>
      )}

      {phase === "error" && (
        <div className="flex flex-col items-center gap-2 py-5 px-4 text-center" data-testid="zoom-dock-error">
          <AlertTriangle size={18} className="text-terracotta" />
          <p className="text-sm text-ink/80">{errorMessage}</p>
          <div className="flex items-center gap-2 mt-1">
            <button onClick={handleJoin} className="text-xs font-medium px-3 py-1.5 rounded-full border border-ink/20 hover:border-ink/40 flex items-center gap-1" data-testid="zoom-dock-retry-button">
              <RotateCcw size={12} /> Retry
            </button>
            {fallbackJoin?.meetingNumber && (
              <a href={externalJoinUrl(fallbackJoin.meetingNumber, fallbackJoin.password)} target="_blank" rel="noopener noreferrer" className="text-xs font-medium px-3 py-1.5 rounded-full border border-ink/20 hover:border-ink/40 flex items-center gap-1" data-testid="zoom-dock-external-fallback-link">
                <ExternalLink size={12} /> Open in Zoom instead
              </a>
            )}
          </div>
        </div>
      )}

      {phase === "in_meeting" && (
        <div className={`flex items-center justify-between px-3 py-1.5 shrink-0 ${isFullscreen ? "bg-ink" : "bg-ink/5"}`}>
          <p className={`text-xs ${isFullscreen ? "text-paper/70" : "text-ink/60"}`}>In meeting</p>
          <div className="flex items-center gap-3">
            <button onClick={toggleFullscreen} className={`text-xs font-medium flex items-center gap-1 ${isFullscreen ? "text-paper/80 hover:text-paper" : "text-ink/60 hover:text-ink"}`} data-testid="zoom-dock-fullscreen-toggle">
              {isFullscreen ? <Minimize size={12} /> : <Maximize size={12} />} {isFullscreen ? "Exit full screen" : "Full screen"}
            </button>
            <button onClick={handleLeave} className="text-xs font-medium text-terracotta flex items-center gap-1" data-testid="zoom-dock-leave-button">
              <PhoneOff size={12} /> Leave
            </button>
          </div>
        </div>
      )}
      {/* The isolated Zoom SDK iframe: only mounted while an attempt is
          in flight or connected, and always remounted (fresh key) on a
          fresh attempt so a stale/failed SDK client is never reused.
          This wrapper — not the iframe itself — is the actual "Zoom
          viewport/container" this task asks for: it owns the box (100%
          width always; aspect-video height by default, or the coach's
          manually dragged height, both capped at 55vh) and the iframe
          simply fills 100% of THAT, never the reverse. */}
      {(phase === "joining" || phase === "in_meeting") && (
        <div
          ref={iframeWrapRef}
          className={isFullscreen ? "relative flex-1" : "relative w-full max-w-full overflow-hidden"}
          style={isFullscreen ? undefined : { height: frameHeight ? `${frameHeight}px` : undefined, aspectRatio: frameHeight ? undefined : "16 / 9", maxHeight: `${MAX_HEIGHT_RATIO * 100}vh` }}
        >
          <iframe
            key={frameKey}
            ref={iframeRef}
            src={ZOOM_EMBED_FRAME_SRC}
            title="Zoom meeting"
            allow="camera; microphone; display-capture"
            data-testid="zoom-dock-component-root"
            style={
              isFullscreen
                ? { position: "absolute", inset: 0, width: "100%", height: "100%", border: 0, display: "block" }
                : { width: "100%", height: "100%", border: 0, display: "block", pointerEvents: isResizing ? "none" : "auto" }
            }
          />
          {phase === "joining" && (
            <div className="absolute inset-0 flex items-center justify-center gap-2 bg-white/90 text-sm text-ink/70" data-testid="zoom-dock-connecting-overlay">
              <Loader2 size={15} className="animate-spin" /> Connecting…
            </div>
          )}
        </div>
      )}

      {phase === "in_meeting" && !isFullscreen && (
        <div
          onPointerDown={startResize}
          onPointerMove={onResizeMove}
          onPointerUp={stopResize}
          onPointerCancel={stopResize}
          className="absolute bottom-1 right-1 w-5 h-2.5 cursor-row-resize text-ink/40 hover:text-ink/70 flex items-center justify-center"
          style={{ touchAction: "none" }}
          data-testid="zoom-dock-resize-handle"
          title="Drag to resize the video area's height"
        >
          <svg viewBox="0 0 16 6" width="16" height="6" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M1 1.5 H15 M1 4.5 H15" strokeLinecap="round" />
          </svg>
        </div>
      )}

      {phase === "mobile_redirected" && (
        <div className="flex flex-col items-center gap-2 py-5" data-testid="zoom-dock-mobile-redirected">
          <p className="text-sm text-ink/70">Opened Zoom in a new tab — come back here anytime, your materials below stay saved.</p>
          <button onClick={handleJoin} className="text-xs font-medium px-3 py-1.5 rounded-full border border-ink/20 hover:border-ink/40 flex items-center gap-1" data-testid="zoom-dock-rejoin-button">
            <RotateCcw size={12} /> Open Zoom again
          </button>
        </div>
      )}

      {phase === "ended" && (
        <div className="flex flex-col items-center gap-2 py-5" data-testid="zoom-dock-ended">
          <p className="text-sm text-ink/70">You&rsquo;ve left the meeting — your materials below are unaffected.</p>
          <button onClick={handleJoin} className="text-xs font-medium px-3 py-1.5 rounded-full border border-ink/20 hover:border-ink/40 flex items-center gap-1" data-testid="zoom-dock-rejoin-button">
            <RotateCcw size={12} /> Rejoin
          </button>
        </div>
      )}
    </div>
  );
}
